import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Inbox, ListChecks, Wallet, MapPin, Phone, Plus, CreditCard } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PoseurDashboard() {
  const [tab, setTab] = useState("disponibles");
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const { data: available = [] } = useQuery({ queryKey: ["leads-available"], queryFn: async () => (await api.get("/leads/available")).data, refetchInterval: 10000 });
  const { data: mine = [] } = useQuery({ queryKey: ["leads-mine"], queryFn: async () => (await api.get("/leads/mine")).data });

  const nav = [
    { key: "disponibles", label: "Leads", icon: Inbox, onClick: () => setTab("disponibles") },
    { key: "mes-leads", label: "Mes leads", icon: ListChecks, onClick: () => setTab("mes-leads") },
    { key: "solde", label: "Solde", icon: Wallet, onClick: () => setTab("solde") },
  ];

  const buy = async (id, mode) => {
    try {
      const { data } = await api.post(`/leads/${id}/buy`, { mode });
      if (data.paid_with === "credit") {
        toast.success(`Lead ${mode === "exclusive" ? "exclusif" : "partagé"} débloqué avec votre avoir`);
        refresh();
        qc.invalidateQueries({ queryKey: ["leads-available"] });
        qc.invalidateQueries({ queryKey: ["leads-mine"] });
        setTab("mes-leads");
      } else {
        const res = await api.post("/payments/checkout", { origin_url: window.location.origin, kind: "lead", reference_id: id, mode });
        window.location.href = res.data.url;
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardShell title="Espace Installateur" nav={nav} active={tab} accent="#F59E0B">
      {tab === "disponibles" && (
        <div>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-[#263871]">Leads d'installation</h2>
              <p className="text-sm text-slate-500">Lead partagé 30€ (max 3 installateurs) · Lead exclusif 60€. Avoir utilisé en priorité.</p>
            </div>
            <Badge className="bg-amber-100 px-3 py-1.5 text-amber-700">Avoir : {user?.credit_balance?.toFixed(2) || "0.00"}€</Badge>
          </div>
          {available.length === 0 ? <Empty text="Aucun lead disponible. Revenez bientôt !" /> : (
            <div className="grid gap-4 sm:grid-cols-2">
              {available.map((l) => {
                const slotsLeft = 3 - (l.buyers_count || 0);
                const canExclusive = (l.buyers_count || 0) === 0;
                return (
                  <div key={l.id} data-testid={`available-lead-${l.id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-[#263871]">{l.client_type === "entreprise" ? "Entreprise" : "Particulier"} · {l.nb_clim} clim</h3>
                      <Badge className="bg-[#E0F2FE] text-[#263871]">{slotsLeft}/3 places</Badge>
                    </div>
                    <p className="mt-2 flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-3.5 w-3.5" />{l.address}</p>
                    <p className="text-xs text-slate-400">
                      {[l.surface && `Surface ${l.surface}`, l.nb_pieces_vivre && `${l.nb_pieces_vivre} pièces`, l.nb_chambres && `${l.nb_chambres} ch.`, l.combles && `Combles: ${l.combles}`].filter(Boolean).join(" · ") || "Projet d'installation"}
                    </p>
                    <p className="mt-1 text-xs italic text-slate-400">Coordonnées masquées jusqu'à l'achat</p>
                    <div className="mt-4 flex flex-col gap-2">
                      <Button data-testid={`buy-shared-${l.id}`} onClick={() => buy(l.id, "shared")} className="w-full rounded-full bg-[#F59E0B] hover:bg-amber-500">
                        Lead partagé — {user?.credit_balance >= 30 ? "avoir 30€" : "30€"}
                      </Button>
                      <Button data-testid={`buy-exclusive-${l.id}`} disabled={!canExclusive} onClick={() => buy(l.id, "exclusive")} variant="outline" className="w-full rounded-full border-2 border-[#263871] text-[#263871] disabled:opacity-40">
                        {canExclusive ? `Lead exclusif — ${user?.credit_balance >= 60 ? "avoir 60€" : "60€"}` : "Exclusif indisponible"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {tab === "mes-leads" && <MyLeads mine={mine} qc={qc} refresh={refresh} />}
      {tab === "solde" && <Solde user={user} mine={mine} />}
    </DashboardShell>
  );
}

function MyLeads({ mine, qc, refresh }) {
  return (
    <div>
      <h2 className="mb-5 text-lg font-bold text-[#263871]">Mes leads débloqués</h2>
      {mine.length === 0 ? <Empty text="Vous n'avez pas encore débloqué de lead." /> : (
        <div className="space-y-4">
          {mine.map((l) => (
            <div key={l.id} data-testid={`my-lead-${l.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-[#263871]">{l.client_name} · {l.nb_clim} clim</h3>
                <Badge variant="outline">{l.status === "converted" ? "Renseigné" : "À traiter"}</Badge>
              </div>
              <p className="mt-1 flex items-center gap-1 text-sm text-slate-600"><Phone className="h-3.5 w-3.5" />{l.client_phone}</p>
              <p className="flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-3.5 w-3.5" />{l.address}</p>
              {l.status !== "converted" && <EnrichForm lead={l} qc={qc} refresh={refresh} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EnrichForm({ lead, qc, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ wants_maintenance: false, nb_clim: lead.nb_clim, notes: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/enrich`, { ...form, nb_clim: Number(form.nb_clim) });
      if (data.reward_avoir) { toast.success(`Bravo ! +${data.reward_avoir}€ d'avoir crédité`); refresh(); }
      else toast.success("Lead renseigné");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["leads-mine"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`enrich-${lead.id}`} variant="outline" className="mt-3 rounded-full border-2"><Plus className="mr-1 h-4 w-4" />Renseigner le lead</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Renseigner le lead</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nombre de clims confirmé</Label><Input data-testid="enrich-nb" type="number" min={1} value={form.nb_clim} onChange={(e) => set("nb_clim", e.target.value)} className="mt-1" /></div>
          <label className="flex items-center gap-2 rounded-xl bg-[#E0F2FE] p-3 text-sm">
            <input type="checkbox" data-testid="enrich-maintenance" checked={form.wants_maintenance} onChange={(e) => set("wants_maintenance", e.target.checked)} />
            Le client veut un contrat d'entretien (+30€ d'avoir pour vous)
          </label>
          <Textarea data-testid="enrich-notes" placeholder="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          <Button data-testid="confirm-enrich" onClick={submit} className="w-full rounded-full bg-[#263871]">Valider</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Solde({ user, mine }) {
  return (
    <div>
      <h2 className="mb-5 text-lg font-bold text-[#263871]">Mon solde</h2>
      <div className="cl-gradient mb-6 rounded-2xl p-8 text-white">
        <p className="text-sky-100">Avoir disponible</p>
        <p className="mt-1 flex items-center gap-2 text-4xl font-extrabold"><Wallet className="h-8 w-8" />{user?.credit_balance?.toFixed(2) || "0.00"}€</p>
        <p className="mt-3 max-w-md text-sm text-sky-100/80">Votre avoir est automatiquement déduit du coût des prochains leads (30€/contact). Renseignez vos leads avec un besoin d'entretien pour gagner des avoirs.</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-bold text-[#263871]">Comment gagner des avoirs ?</h3>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li className="flex gap-2"><CreditCard className="h-4 w-4 text-[#1D9BF0]" />Renseignez un lead pour signaler un besoin de contrat d'entretien : +30€</li>
          <li className="flex gap-2"><CreditCard className="h-4 w-4 text-[#1D9BF0]" />L'avoir évite de payer le prochain contact</li>
        </ul>
        <p className="mt-4 text-sm text-slate-500">Leads débloqués : <strong>{mine.length}</strong></p>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">{text}</div>;
}
